export const filterRolesByType = (roles, roleType) => {
  return roles?.filter((role) => role.role_type === roleType) || []
}

export const getAvailableRolesForReassignment = (roles, excludeRoleId) => {
  return roles?.filter((r) => r.id !== excludeRoleId) || []
}

export const filterPermissionsByColumn = (permissionsData, column) => {
  return permissionsData?.filter((p) => p.column === column) || []
}

export const createRoleMenuItems = (role, handlers, t, Icon, styles) => ({
  items: [
    {
      key: "edit",
      label: (
        <div className={styles.menuItem}>
          <Icon type="feather" name="Edit2" width={16} height={16} />
          <span>{t("staff.roles_access.edit")}</span>
        </div>
      ),
      onClick: () => handlers.onEdit(role)
    },
    {
      key: "delete",
      label: (
        <div className={styles.menuItemDelete}>
          <Icon type="feather" name="Trash2" width={16} height={16} color="var(--color-error)" />
          <span className={styles.menuItemDeleteText}>{t("staff.roles_access.delete")}</span>
        </div>
      ),
      onClick: () => handlers.onDelete(role)
    }
  ]
})

export const transformPermissionHashToUI = (permissionHash) => {
  if (!permissionHash) {
    return []
  }

  const modules = Array.isArray(permissionHash) ? permissionHash : Object.values(permissionHash)

  return modules.map((module, index) => {
    const moduleName = module?.label || `Module ${module?.access_module_id}`
    const moduleId = module?.label?.toLowerCase().replace(/\s+/g, "_") || `module_${module?.access_module_id}`

    const availableOptions = module?.available_options || {}
    const permissionSettings = module?.permission_settings || {}

    const hasScreens = Object.values(availableOptions).some((opt) => opt && typeof opt === "object" && opt.actions)

    const subPermissions = []
    let isEnabled = false

    if (hasScreens) {
      Object.entries(availableOptions).forEach(([screenKey, screenConfig]) => {
        const actions = screenConfig?.actions || {}
        Object.entries(actions).forEach(([actionKey, actionConfig]) => {
          const permissionLabel = actionConfig?.label || actionKey.replace(/_/g, " ")
          const subPermId = `${moduleId}-${actionKey}`

          const setting = permissionSettings[screenKey]?.actions?.[actionKey]
          const active = setting?.is_active === true

          const subPerm = {
            id: subPermId,
            label: permissionLabel,
            key: actionKey,
            screenKey: screenKey,
            enabled: active,
            permission_id: setting?.id,
            access_screen_id: screenConfig?.access_screen_id || screenConfig?.id
          }

          if (actionConfig?.scopes && Array.isArray(actionConfig.scopes) && actionConfig.scopes.length > 1) {
            subPerm.options = actionConfig.scopes.map((scopeItem) => scopeItem.label)

            if (active && setting?.scope) {
              const scopeItem = actionConfig.scopes.find((s) => s.value === setting.scope)
              subPerm.selectedScope = scopeItem ? scopeItem.label : subPerm.options[0] || ""
            }
          }

          subPermissions.push(subPerm)
          if (active) isEnabled = true
        })
      })
    } else {
      const hasOnlyFullAccess = Object.keys(availableOptions).length === 1 && availableOptions.full_access !== undefined

      if (!hasOnlyFullAccess) {
        Object.entries(availableOptions)?.forEach(([permKey, permConfig]) => {
          const permissionLabel = permConfig?.label || permKey.replace(/_/g, " ")
          const subPermId = `${moduleId}-${permKey}`

          const subPerm = {
            id: subPermId,
            label: permissionLabel,
            key: permConfig?.value || permKey,
            enabled: permissionSettings?.[permKey] !== undefined && permissionSettings?.[permKey] !== false
          }

          if (permConfig?.scope && Array.isArray(permConfig.scope) && permConfig.scope.length > 1) {
            subPerm.options = permConfig.scope.map((scopeItem) => scopeItem.label)

            const currentSetting = permissionSettings?.[permKey]
            if (currentSetting && currentSetting !== false) {
              const scopeItem = permConfig.scope.find((s) => s.value === currentSetting)
              subPerm.selectedScope = scopeItem ? scopeItem.label : subPerm.options[0] || ""
            }
          }

          subPermissions.push(subPerm)
        })
      }

      isEnabled = hasOnlyFullAccess ? permissionSettings?.full_access === true : Object.keys(permissionSettings || {}).length > 0
    }

    const column = index % 2 === 0 ? "left" : "right"
    const hasOnlyFullAccess = subPermissions.length === 1 && (subPermissions[0].key === "full_access" || subPermissions[0].key === "full")

    return {
      id: moduleId,
      access_module_id: module?.access_module_id,
      title: moduleName,
      column,
      iconName: getModuleIcon(moduleName),
      iconType: getModuleIconType(moduleName),
      subPermissions,
      hasOnlyFullAccess,
      isEnabled: hasOnlyFullAccess ? subPermissions[0].enabled : isEnabled
    }
  })
}

const getModuleIcon = (moduleName) => {
  const iconMap = {
    bookings: "booking",
    calendar: "calendar",
    clients: "user",
    messages: "messages",
    gallery: "gallery",
    marketing: "marketing",
    accounting: "finances",
    crew: "staff",
    equipment: "settings",
    experiences: "experiences",
    rentals: "rental"
  }

  const normalizedName = moduleName.toLowerCase()
  return iconMap[normalizedName] || "settings"
}

const getModuleIconType = (moduleName) => {
  const featherIcons = ["clients", "crew", "equipment", "rentals"]
  const normalizedName = moduleName.toLowerCase()
  return featherIcons.includes(normalizedName) ? "feather" : "dsicon"
}

export const getInitialExpandedFromPermissions = (permissionHash) => {
  if (!permissionHash) {
    return {}
  }

  const modules = Array.isArray(permissionHash) ? permissionHash : Object.values(permissionHash)

  const expanded = {}
  modules.forEach((module) => {
    const moduleId = module?.label?.toLowerCase().replace(/\s+/g, "_") || `module_${module?.access_module_id}`
    const settings = module?.permission_settings || {}

    let hasActive = false
    if (Object.values(settings).some((s) => s && typeof s === "object" && s.actions)) {
      hasActive = Object.values(settings).some((screen) => Object.values(screen?.actions || {}).some((action) => action?.is_active))
    } else {
      hasActive = Object.keys(settings).length > 0
    }

    expanded[moduleId] = hasActive
  })

  return expanded
}

export const getInitialSelectedTabsFromPermissions = (permissionHash) => {
  if (!permissionHash) {
    return {}
  }

  const modules = Array.isArray(permissionHash) ? permissionHash : Object.values(permissionHash)

  const selectedTabs = {}
  modules.forEach((module) => {
    const moduleId = module?.label?.toLowerCase().replace(/\s+/g, "_") || `module_${module?.access_module_id}`
    const settings = module?.permission_settings || {}
    const availableOptions = module?.available_options || {}

    const hasScreens = Object.values(availableOptions).some((opt) => opt && typeof opt === "object" && opt.actions)

    if (hasScreens) {
      Object.entries(availableOptions).forEach(([screenKey, screenConfig]) => {
        const actions = screenConfig?.actions || {}
        Object.entries(actions).forEach(([actionKey, actionConfig]) => {
          const tabId = `${moduleId}-${actionKey}`
          const setting = settings[screenKey]?.actions?.[actionKey]

          if (setting?.is_active && setting?.scope && actionConfig.scopes) {
            const scopeItem = actionConfig.scopes.find((s) => s.value === setting.scope)
            const options = actionConfig.scopes.map((s) => s.label)
            selectedTabs[tabId] = scopeItem ? scopeItem.label : options[0] || ""
          }
        })
      })
    } else {
      Object.entries(settings).forEach(([permKey, permValue]) => {
        const tabId = `${moduleId}-${permKey}`

        if (permValue && permValue !== false) {
          const permConfig = availableOptions[permKey]
          if (permConfig && permConfig.scope && Array.isArray(permConfig.scope)) {
            const scopeItem = permConfig.scope.find((s) => s.value === permValue)
            const options = permConfig.scope.map((s) => s.label)
            selectedTabs[tabId] = scopeItem ? scopeItem.label : options[0] || ""
          }
        }
      })
    }
  })

  return selectedTabs
}

export const buildPermissionPayload = (module, permissionKey, isEnabled, scopeValue = null) => {
  const payload = {
    id: module.id,
    access_module_id: module.access_module_id,
    permission_settings: {}
  }
  const currentSettings = module.permission_settings || {}

  Object.keys(currentSettings).forEach((key) => {
    const setting = currentSettings[key]
    if (typeof setting === "object" && setting.scope) {
      payload.permission_settings[key] = setting.scope
    } else {
      payload.permission_settings[key] = setting
    }
  })

  if (isEnabled) {
    if (scopeValue !== null) {
      payload.permission_settings[permissionKey] = scopeValue
    } else {
      payload.permission_settings[permissionKey] = true
    }
  } else {
    delete payload.permission_settings[permissionKey]
  }

  return payload
}

export const buildCompletePermissionPayload = (originalModule, subPermissions, selectedTabs, targetPermId = null, targetValue = null) => {
  const isNewStructure = Object.values(originalModule?.available_options || {}).some((opt) => opt && typeof opt === "object" && opt.actions)

  const payload = {
    id: originalModule.id,
    access_module_id: originalModule.access_module_id,
    permission_settings: {}
  }

  if (isNewStructure) {
    Object.keys(originalModule.available_options).forEach((screenKey) => {
      payload.permission_settings[screenKey] = {
        actions: {}
      }
    })

    subPermissions.forEach((sp) => {
      const isTargetPerm = targetPermId ? sp.id === targetPermId : false
      const screenKey = sp.screenKey || Object.keys(originalModule.available_options)[0]

      let active = false
      let scope = null

      if (isTargetPerm) {
        if (typeof targetValue === "boolean") {
          active = targetValue
          if (active) {
            const scopeLabel = selectedTabs[sp.id] || sp.selectedScope || (sp.options && sp.options[0])
            if (scopeLabel) {
              const scopeItem = originalModule.available_options?.[screenKey]?.actions?.[sp.key]?.scopes?.find(
                (s) => s.label === scopeLabel
              )
              scope = scopeItem?.value || scopeLabel
            }
          }
        } else {
          active = true
          scope = targetValue
        }
      } else if (sp.enabled) {
        active = true
        if (sp.options && sp.options.length > 0) {
          const scopeLabel = selectedTabs[sp.id] || sp.selectedScope || sp.options[0]
          const scopeItem = originalModule.available_options?.[screenKey]?.actions?.[sp.key]?.scopes?.find((s) => s.label === scopeLabel)
          scope = scopeItem?.value || scopeLabel
        }
      }

      if (!payload.permission_settings[screenKey]) {
        payload.permission_settings[screenKey] = { actions: {} }
      }

      payload.permission_settings[screenKey].actions[sp.key] = {
        active,
        scope
      }
    })
  } else {
    subPermissions.forEach((sp) => {
      const isTargetPerm = targetPermId ? sp.id === targetPermId : false

      if (isTargetPerm && targetValue !== null) {
        payload.permission_settings[sp.key] = targetValue
      } else if (sp.enabled || isTargetPerm) {
        if (sp.options && sp.options.length > 0) {
          const scopeLabel = selectedTabs[sp.id] || sp.selectedScope || sp.options[0]
          const scopeItem = originalModule.available_options?.[sp.key]?.scope?.find((s) => s.label === scopeLabel)
          payload.permission_settings[sp.key] = scopeItem?.value || scopeLabel
        } else {
          payload.permission_settings[sp.key] = true
        }
      } else {
        payload.permission_settings[sp.key] = false
      }
    })
  }

  return payload
}

export const buildAssignPermissionPayload = (accessModuleId, subPerm, isActive, scope) => {
  return {
    role: {
      permissions_attributes: [
        {
          id: subPerm.permission_id,
          access_module_id: accessModuleId,
          access_screen_id: subPerm.access_screen_id,
          action_name: subPerm.key,
          is_active: isActive,
          scope: scope
        }
      ]
    }
  }
}
